import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';

export const SITE_URL = 'https://www.toletforrent.com';
export const SITE_NAME = 'Toletforrent';
export const DEFAULT_TITLE =
  'Toletforrent — Rent rooms, PGs, flats & commercial spaces. Zero brokerage.';
export const DEFAULT_DESCRIPTION =
  "Toletforrent — India's zero-brokerage rental marketplace. Find verified rooms, PGs, flats, houses and commercial spaces directly from owners across 100+ cities.";
export const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

/**
 * Standard Organization & Website JSON-LD Schema
 */
export const defaultOrganizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/assets/images/logo.svg`,
  description: DEFAULT_DESCRIPTION,
  sameAs: [
    'https://www.facebook.com/toletforrent',
    'https://twitter.com/toletforrent',
    'https://www.instagram.com/toletforrent',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    email: 'support@toletforrent.com',
    url: `${SITE_URL}/help-center`,
  },
};

/**
 * Helper to generate RealEstateListing Schema for property pages (AI & Google rich snippets)
 */
export const buildRealEstateListingSchema = (prop) => {
  if (!prop) return null;
  const propId = prop._id || prop.id || '';
  const title = prop.title || 'Rental Property';
  const description = prop.description || `${title} available for rent on Toletforrent with 0% brokerage.`;
  const price = prop.rent || prop.dailyPrice || 0;
  const image = prop.primaryImageUrl || (Array.isArray(prop.images) && prop.images[0]) || DEFAULT_IMAGE;
  const city = prop.city || prop.address || 'India';
  const address = prop.address || prop.locationText || city;

  return {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    name: title,
    description: description,
    url: `${SITE_URL}/p/${propId}`,
    image: image,
    datePosted: prop.createdAt || new Date().toISOString(),
    offers: {
      '@type': 'Offer',
      price: price,
      priceCurrency: 'INR',
      availability: 'https://schema.org/InStock',
      validFrom: new Date().toISOString(),
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: city,
      streetAddress: address,
      addressCountry: 'IN',
    },
  };
};

/**
 * Helper to generate FAQPage Schema (crucial for ChatGPT / Perplexity / AI search engines)
 */
export const buildFAQSchema = (faqList) => {
  if (!Array.isArray(faqList) || faqList.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqList.map((item) => ({
      '@type': 'Question',
      name: item.question || item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer || item.a,
      },
    })),
  };
};

/**
 * Helper to generate BreadcrumbList Schema
 */
export const buildBreadcrumbSchema = (breadcrumbs) => {
  if (!Array.isArray(breadcrumbs) || breadcrumbs.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: crumb.name,
      item: crumb.url.startsWith('http') ? crumb.url : `${SITE_URL}${crumb.url}`,
    })),
  };
};

/**
 * Main drop-in SEO component for React Helmet
 */
const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords,
  image = DEFAULT_IMAGE,
  canonical,
  noindex = false,
  rawTitle = false,
  jsonLd,
  children,
}) => {
  const location = useLocation();
  const path = canonical || location?.pathname || '/';
  const canonicalUrl = path.startsWith('http')
    ? path
    : `${SITE_URL}${path === '/' ? '' : path}`;

  const fullTitle = !title
    ? DEFAULT_TITLE
    : rawTitle
    ? title
    : `${title} | ${SITE_NAME}`;

  const finalJsonLd = jsonLd ? jsonLd : defaultOrganizationSchema;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />
      <meta
        name="robots"
        content={noindex ? 'noindex, nofollow' : 'index, follow'}
      />
      <meta name="author" content="Toletforrent Technologies" />
      <meta name="application-name" content={SITE_NAME} />
      <meta name="apple-mobile-web-app-title" content={SITE_NAME} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Structured JSON-LD Data */}
      {finalJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(finalJsonLd)}
        </script>
      )}
      {children}
    </Helmet>
  );
};

export default SEO;
